<template>
  <div class="copy-link-box">
    <div 
      class="copy-link-wrap"
      :class="{ pressed: isPressed }"
      :aria-label="tooltip"
      :aria-disabled="false"
      :data-tooltip="tooltip"
      @mousedown="handlePress(true)"
      @mouseup="handlePress(false)"
      @mouseleave="handlePress(false)"
      @touchstart="handlePress(true)"
      @touchend="handlePress(false)"
      @touchcancel="handlePress(false)"
    >
      <a 
        class="copy-link"
        :href="href"
        :aria-label="tooltip"
        role="button"
        :data-tooltip="tooltip"
        @click.prevent="handleCopy"
      >
        <span>
          <svg 
            class="OUGEr QdAdhf" 
            width="22px" 
            height="22px" 
            viewBox="0 0 24 24" 
            fill="currentColor"
            focusable="false"
          >
            <path d="M0 0h24v24H0z" fill="none"></path>
            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"></path>
          </svg>
        </span>
      </a>
    </div>
    <span class="name-text">{{ nameText }}</span>
  </div>
</template>

<script>
import { copyLinkBus } from '../utils/event-bus';

/**
 * CopyLinkButton - 复制链接按钮组件
 * 
 * 功能：
 * - 渲染复制链接按钮（SVG 图标）
 * - 处理 hover/press 交互状态
 * - 点击时复制完整 URL 到剪贴板
 * - 通过 EventBus 发送复制成功事件
 */
export default {
  name: 'CopyLinkButton',
  
  props: {
    /**
     * 标题文本（用于生成 tooltip）
     */
    nameText: {
      type: String,
      required: true
    },
    
    /**
     * 锚点 ID
     */
    anchorId: {
      type: String,
      required: true
    },
    
    /**
     * 链接地址（通常是 #anchorId）
     */
    href: {
      type: String,
      default: '#'
    }
  },
  
  data() {
    return {
      isPressed: false,
      tooltip: '复制标题链接'
    };
  },
  
  methods: {
    /**
     * 处理按下/释放状态
     * @param {boolean} isPressed - 是否按下
     */
    handlePress(isPressed) {
      this.isPressed = isPressed;
    },
    
    /**
     * 处理复制操作
     */
    async handleCopy() {
      const fullUrl = this.href.startsWith('#')
        ? window.location.href.split('#')[0] + this.href
        : this.href;
      
      const success = await this.copyToClipboard(fullUrl);
      if (success) {
        // 通过 EventBus 发送事件，解耦组件通信
        copyLinkBus.emit('copy', fullUrl);
      }
    },
    
    /**
     * 复制文本到剪贴板
     * @param {string} text - 要复制的文本
     * @returns {Promise<boolean>} - 是否复制成功
     */
    async copyToClipboard(text) {
      try {
        // 优先使用现代 Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (err) {
        console.warn('[CopyLinkButton] Clipboard API failed:', err);
      }
      
      // 降级方案：使用 execCommand
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const result = document.execCommand('copy');
        document.body.removeChild(textArea);
        return result;
      } catch (err) {
        console.error('[CopyLinkButton] execCommand copy failed:', err);
        return false;
      }
    }
  }
};
</script>

<style scoped>
/* 样式由全局 CSS 管理，此处不需要 scoped styles */
/* 保持与原有 .copy-link-wrap 和 .copy-link 类名兼容 */
</style>
